import { axiosDZAuthInstance } from '../axios'
import {
  TBulkPromoCodeCampaign,
  TBulkPromoCodeCreateRequest,
  TPromoCodeListItem,
} from '@/modules/BulkPromoCodes/types'
import { GetListParams } from 'react-admin'
import { paramsMapper, responseMapper } from '../helpers'
import { TMeta } from '@/types/data'

// Response type from server
interface TBulkPromoCodesListResponse {
  data: TBulkPromoCodeCampaign[]
  meta: TMeta
}

export const createBulkPromoCodeCampaign = async (
  data: TBulkPromoCodeCreateRequest,
): Promise<TBulkPromoCodeCampaign> => {
  try {
    const { data: response } = await axiosDZAuthInstance.post<TBulkPromoCodeCampaign>(
      '/dashboard/bulk-promo-codes',
      data,
    )
    return response
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to create bulk promo code campaign'
    throw new Error(errorMessage)
  }
}

export const getBulkPromoCodeCampaigns = async (
  params: GetListParams,
): Promise<{ data: TBulkPromoCodeCampaign[]; total: number }> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TBulkPromoCodesListResponse>('/dashboard/bulk-promo-codes', {
      params: paramsMapper(params),
    })
    // Use responseMapper pattern for consistency
    return responseMapper(data)
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to fetch bulk promo code campaigns'
    throw new Error(errorMessage)
  }
}

export const getBulkPromoCodeCampaign = async (id: number): Promise<TBulkPromoCodeCampaign> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TBulkPromoCodeCampaign | { data: TBulkPromoCodeCampaign }>(`/dashboard/bulk-promo-codes/${id}`)
    // Handle both wrapped and unwrapped response formats
    const campaign = 'data' in data && data.data && typeof data.data === 'object' && 'id' in data.data 
      ? data.data 
      : data as TBulkPromoCodeCampaign
    return campaign
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Campaign not found')
    }
    const errorMessage = error.response?.data?.message || 'Failed to fetch bulk promo code campaign'
    throw new Error(errorMessage)
  }
}

export const getBulkPromoCodeCampaignCodes = async (
  id: number,
  params: { offset?: number; limit?: number } = {},
): Promise<TPromoCodeListItem> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TPromoCodeListItem>(
      `/dashboard/bulk-promo-codes/${id}/promo-codes`,
      { params },
    )
    return data
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to fetch promo codes'
    throw new Error(errorMessage)
  }
}

export const deleteBulkPromoCodeCampaign = async (id: number): Promise<void> => {
  try {
    await axiosDZAuthInstance.delete(`/dashboard/bulk-promo-codes/${id}`)
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Campaign not found')
    }
    const errorMessage = error.response?.data?.message || 'Failed to delete bulk promo code campaign'
    throw new Error(errorMessage)
  }
}

/**
 * Update bulk promo code campaign settings
 * Note: Generation criteria (prefix, codeLength, totalCodesToGenerate) cannot be modified
 */
export interface TBulkPromoCodeUpdateRequest {
  name?: string
  promoRuleId?: string
  promoRuleVersion?: number
  expiryDate?: string
  isActive?: boolean
  maxRegisterDate?: string | null
  neverActiveUsers?: boolean
  platform?: string
  promoCodableIds?: number[]
  promoCodableType?: 'BRAND' | 'PRODUCT' | 'VARIANT'
}

export const updateBulkPromoCodeCampaign = async (
  id: number,
  data: TBulkPromoCodeUpdateRequest,
): Promise<TBulkPromoCodeCampaign> => {
  try {
    const { data: response } = await axiosDZAuthInstance.patch<TBulkPromoCodeCampaign>(
      `/dashboard/bulk-promo-codes/${id}`,
      data,
    )
    return response
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Campaign not found')
    }
    const errorMessage = error.response?.data?.message || 'Failed to update bulk promo code campaign'
    throw new Error(errorMessage)
  }
}

/**
 * Continue/resume generation for a failed or incomplete campaign
 * Re-queues the generation job to pick up from where it left off
 */
export const continueGeneration = async (id: number): Promise<void> => {
  try {
    await axiosDZAuthInstance.post(`/dashboard/bulk-promo-codes/${id}/continue`)
  } catch (error: any) {
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Cannot continue this campaign')
    }
    if (error.response?.status === 404) {
      throw new Error('Campaign not found')
    }
    const errorMessage = error.response?.data?.message || 'Failed to continue generation'
    throw new Error(errorMessage)
  }
}

/**
 * Check if a specific quantity of codes can be generated for a given prefix and code length
 * @param prefix - The prefix to check
 * @param codeLength - Total code length
 * @param requestedQuantity - Number of codes to generate
 * @returns Whether the request can be fulfilled (enough codes available)
 */
export const checkCanFulfill = async (
  prefix: string, 
  codeLength: number, 
  requestedQuantity: number
): Promise<boolean> => {
  try {
    const availability = await getCodeAvailability(prefix, codeLength, requestedQuantity)
    return availability.canFulfill ?? (requestedQuantity <= availability.availableCount)
  } catch (error: any) {
    console.error('Failed to check availability:', error)
    // Let backend validate on submit
    return true
  }
}

export interface TCodeAvailabilityResponse {
  existingCount: number
  totalPossible: number
  availableCount: number
  requestedQuantity?: number
  canFulfill: boolean
}

export enum CsvExportStatus {
  READY = 'READY',
  GENERATING = 'GENERATING',
  NOT_STARTED = 'NOT_STARTED',
}

export interface TDownloadUrlResponse {
  status: CsvExportStatus
  downloadUrl?: string
  expiresAt?: string
  expiresIn?: number
  message?: string
  totalCodes?: number
}

/**
 * Get a presigned download URL for the CSV file or trigger CSV generation
 * If CSV is not ready, it will trigger generation and return status
 */
export const getDownloadUrl = async (id: number): Promise<TDownloadUrlResponse> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TDownloadUrlResponse>(
      `/dashboard/bulk-promo-codes/${id}/download`,
    )
    return data
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to get download URL'
    throw new Error(errorMessage)
  }
}

/**
 * Trigger CSV generation for a campaign
 */
export const generateCsv = async (id: number): Promise<TDownloadUrlResponse> => {
  try {
    const { data } = await axiosDZAuthInstance.post<TDownloadUrlResponse>(
      `/dashboard/bulk-promo-codes/${id}/generate-csv`,
    )
    return data
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to generate CSV'
    throw new Error(errorMessage)
  }
}

export const getCodeAvailability = async (
  prefix: string,
  codeLength: number,
  requestedQuantity?: number,
): Promise<TCodeAvailabilityResponse> => {
  try {
    const params: Record<string, any> = { prefix, codeLength }
    if (requestedQuantity !== undefined) {
      params.requestedQuantity = requestedQuantity
    }
    const { data } = await axiosDZAuthInstance.get<TCodeAvailabilityResponse>(
      '/dashboard/bulk-promo-codes/availability',
      { params },
    )
    return data
  } catch (error: any) {
    // If API doesn't exist, calculate locally (fallback)
    // Character set excludes confusing characters (0, O, 1, I, L): ABCDEFGHJKLMNPQRSTUVWXYZ23456789
    const randomLength = codeLength - prefix.length
    const charsetSize = 32 // 24 letters + 8 digits (excludes 0, O, 1, I, L)
    const totalPossible = Math.pow(charsetSize, randomLength)
    const availableCount = totalPossible
    return {
      existingCount: 0,
      totalPossible,
      availableCount,
      requestedQuantity,
      canFulfill: requestedQuantity ? requestedQuantity <= availableCount : true,
    }
  }
}
